import { Link } from "react-router-dom";
import {
  ArrowRight, BadgeCheck, BarChart3, BellRing, BriefcaseBusiness, CalendarClock,
  FileText, Inbox, LayoutDashboard, Mail, Megaphone, MessageSquareText,
  Sparkles, UsersRound, Workflow,
} from "lucide-react";
import Seo from "../components/Seo";

const PILL = "text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded";

function Panel({ icon: Icon, title, children, testid }) {
  return (
    <div className="border border-zinc-200 rounded-md bg-white overflow-hidden flex flex-col" data-testid={testid}>
      <div className="flex items-center gap-2 px-5 pt-5 pb-3 border-b border-zinc-100">
        <Icon className="h-4 w-4 text-blue-700" />
        <p className="text-sm font-bold text-zinc-950">{title}</p>
        <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Example view</span>
      </div>
      <div className="p-5 flex-1">{children}</div>
    </div>
  );
}

export default function Platform() {
  return (
    <main data-testid="platform-page">
      <Seo
        title="HiredToday for Employers | One Platform to Hire Staff in the UK"
        description="Post a job free and run the whole hire in one place: vacancy management, candidate pipeline, recruitment analytics, interviews, team collaboration and AI tools. Affordable job advertising for UK employers."
        path="/platform"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "HiredToday for Employers",
          description: "How HiredToday brings vacancy management, candidate pipelines, recruitment analytics, interviews, team collaboration, candidate communications, AI tools and job distribution into one UK hiring platform.",
        }}
      />

      <section className="relative overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0">
          <div className="liquid-blob blob-a w-[460px] h-[460px] -top-32 -left-24" style={{ background: "#BFDBFE" }} />
          <div className="liquid-blob blob-b w-[380px] h-[380px] top-24 -right-24" style={{ background: "#FDE68A" }} />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-16 sm:pb-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4" data-testid="platform-eyebrow">HiredToday for employers</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-zinc-950 leading-[1.04]" data-testid="platform-h1">
            Hiring, run from one place.
          </h1>
          <p className="mt-6 text-sm sm:text-base text-zinc-600 max-w-2xl leading-relaxed" data-testid="platform-intro">
            Most small businesses hire across a patchwork of job boards, spreadsheets, inboxes and calendars.
            HiredToday puts the whole hire in one place, advertise a job for free, watch applicants arrive,
            move them through a clear pipeline, interview, offer and hire. No separate logins, no lost CVs,
            no paying for visibility you don't need.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/post-a-job" data-testid="platform-cta-post"
              className="h-12 px-8 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 inline-flex items-center justify-center gap-2">
              Post a job free <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/pricing" data-testid="platform-cta-pricing"
              className="h-12 px-8 bg-white border border-zinc-300 text-zinc-900 text-sm font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200 inline-flex items-center justify-center">
              See pricing
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-4" data-testid="platform-replaces">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { icon: Inbox, label: "Instead of a crowded inbox", note: "Every application lands in one pipeline, with the CV attached." },
            { icon: FileText, label: "Instead of spreadsheets", note: "Stages, notes and history stay on the candidate, not in a sheet." },
            { icon: Megaphone, label: "Instead of five job boards", note: "One listing reaches search, Google Jobs and partner boards." },
            { icon: CalendarClock, label: "Instead of diary ping-pong", note: "Interviews are proposed, confirmed and reminded in one thread." },
          ].map((x) => (
            <div key={x.label} className="glass rounded-lg p-5" data-testid={`platform-replaces-${x.label.split(" ")[3] || x.label.split(" ")[2]}`}>
              <x.icon className="h-5 w-5 text-zinc-700" />
              <p className="mt-3 text-sm font-semibold text-zinc-950">{x.label}</p>
              <p className="mt-1 text-xs text-zinc-500 leading-relaxed">{x.note}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="platform-tour">
        <h2 className="font-display text-2xl sm:text-3xl tracking-tight font-black text-zinc-950">A look inside</h2>
        <p className="mt-3 text-sm text-zinc-600 max-w-2xl leading-relaxed">
          These are simplified example views of the real screens your team would use, shown with demonstration
          data only. Every one of them is included from the free listing upwards; paid options only add reach.
        </p>

        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <Panel icon={LayoutDashboard} title="Employer dashboard" testid="platform-panel-dashboard">
            <div className="grid grid-cols-3 gap-2 mb-4">
              {[["Live roles", 3], ["New applications", 12], ["Interviews", 4]].map(([l, v]) => (
                <div key={l} className="border border-zinc-100 rounded-md p-3 bg-zinc-50/60">
                  <p className="font-display text-xl font-black text-zinc-950">{v}</p>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mt-0.5">{l}</p>
                </div>
              ))}
            </div>
            {[
              ["Warehouse Operative", "live", "bg-green-100 text-green-700", "8 applicants"],
              ["Class 1 HGV Driver", "live", "bg-green-100 text-green-700", "4 applicants"],
              ["Shift Supervisor", "in review", "bg-amber-100 text-amber-700", "goes live shortly"],
            ].map(([t, s, c, n]) => (
              <div key={t} className="flex items-center justify-between py-2.5 border-t border-zinc-100">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-900 truncate">{t}</p>
                  <p className="text-xs text-zinc-400">{n}</p>
                </div>
                <span className={`${PILL} ${c}`}>{s}</span>
              </div>
            ))}
            <p className="text-xs text-zinc-500 mt-4 leading-relaxed">
              Every vacancy, applicant and message for your company, one screen, with status, SEO and listing health per role.
            </p>
          </Panel>

          <Panel icon={Workflow} title="Candidate pipeline" testid="platform-panel-pipeline">
            <div className="grid grid-cols-4 gap-2">
              {[
                ["New", ["A. Smith", "J. Okafor"]],
                ["Reviewing", ["M. Patel"]],
                ["Interview", ["L. Jones"]],
                ["Offer", ["K. Brown"]],
              ].map(([stage, people]) => (
                <div key={stage} className="bg-zinc-50/70 border border-zinc-100 rounded-md p-2.5">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-2">{stage}</p>
                  {people.map((p) => (
                    <p key={p} className="text-xs font-medium text-zinc-800 bg-white border border-zinc-200 rounded px-2 py-1.5 mb-1.5">{p}</p>
                  ))}
                </div>
              ))}
            </div>
            <p className="text-xs text-zinc-500 mt-4 leading-relaxed">
              Move applicants between stages and the candidate is emailed automatically, politely, and only when something actually changes.
            </p>
          </Panel>

          <Panel icon={BarChart3} title="Recruitment analytics" testid="platform-panel-analytics">
            <div className="flex items-end gap-2 h-24 mb-3" aria-hidden="true">
              {[35, 55, 40, 70, 62, 88, 76].map((h, i) => (
                <div key={`bar-${i}`} className="flex-1 bg-blue-100 rounded-sm" style={{ height: `${h}%` }}>
                  <div className="bg-blue-600 rounded-sm w-full" style={{ height: "35%" }} />
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 text-xs text-zinc-600">
              {["Direct", "Google Jobs", "Adzuna", "Reed", "Social"].map((s) => (
                <span key={s} className="bg-zinc-100 rounded-full px-2.5 py-1 font-medium">{s}</span>
              ))}
            </div>
            <p className="text-xs text-zinc-500 mt-4 leading-relaxed">
              See views, apply starts and completed applications per vacancy, broken down by where candidates actually came from, so you know which channel earns its money.
            </p>
          </Panel>

          <Panel icon={Megaphone} title="Job distribution" testid="platform-panel-distribution">
            <div className="space-y-2.5">
              <div className="flex items-center gap-2.5 border border-zinc-100 rounded-md p-3">
                <BadgeCheck className="h-4 w-4 text-blue-700 shrink-0" />
                <p className="text-xs text-zinc-700"><span className="font-semibold text-zinc-950">Google Jobs:</span> every approved listing is structured and notified for indexing automatically.</p>
              </div>
              <div className="flex items-center gap-2.5 border border-zinc-100 rounded-md p-3">
                <BadgeCheck className="h-4 w-4 text-blue-700 shrink-0" />
                <p className="text-xs text-zinc-700"><span className="font-semibold text-zinc-950">Location boost:</span> your role surfaces in searches for up to 10 real nearby towns, suggested for you and editable.</p>
              </div>
              <div className="flex items-center gap-2.5 border border-zinc-100 rounded-md p-3">
                <BadgeCheck className="h-4 w-4 text-blue-700 shrink-0" />
                <p className="text-xs text-zinc-700"><span className="font-semibold text-zinc-950">Partner job boards:</span> optional, per vacancy and always with your explicit consent. Applications still come back to HiredToday.</p>
              </div>
            </div>
            <p className="text-xs text-zinc-500 mt-4 leading-relaxed">
              Write the vacancy once. Distribution, structured data and indexing are handled for you, and you choose how far each role travels.
            </p>
          </Panel>

          <Panel icon={UsersRound} title="Team collaboration" testid="platform-panel-team">
            <div className="space-y-2">
              {[
                ["D. Owner", "Owner", "Full control, billing and team"],
                ["S. Harris", "Admin", "Manages jobs and applicants"],
                ["T. Recruitment", "Recruiter", "Works the pipeline day to day"],
                ["P. Viewer", "Viewer", "Read-only oversight"],
              ].map(([n, r, d]) => (
                <div key={n} className="flex items-center gap-3 border border-zinc-100 rounded-md p-3">
                  <div className="h-8 w-8 rounded-full bg-zinc-100 flex items-center justify-center text-xs font-bold text-zinc-600 shrink-0">{n.charAt(0)}</div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-zinc-900">{n}</p>
                    <p className="text-xs text-zinc-400 truncate">{d}</p>
                  </div>
                  <span className={`${PILL} bg-zinc-100 text-zinc-600`}>{r}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-zinc-500 mt-4 leading-relaxed">
              Invite colleagues with owner, admin, recruiter or viewer roles, server-enforced, so the right people see the right things.
            </p>
          </Panel>

          <Panel icon={CalendarClock} title="Interviews" testid="platform-panel-interviews">
            <div className="border border-zinc-100 rounded-md p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-zinc-950">L. Jones, Warehouse Operative</p>
                <span className={`${PILL} bg-green-100 text-green-700`}>Confirmed</span>
              </div>
              <p className="text-xs text-zinc-500">Tuesday 10:30 · Google Meet link attached · reminder emails scheduled automatically</p>
              <div className="flex gap-2">
                <span className="text-xs font-medium bg-zinc-100 rounded-full px-2.5 py-1 text-zinc-600">Invite emailed</span>
                <span className="text-xs font-medium bg-zinc-100 rounded-full px-2.5 py-1 text-zinc-600">Reminder 24h before</span>
              </div>
            </div>
            <p className="text-xs text-zinc-500 mt-4 leading-relaxed">
              Propose times, attach your own Meet or Teams link, and both sides get confirmed details and reminders. No calendar back-and-forth.
            </p>
          </Panel>

          <Panel icon={MessageSquareText} title="Candidate communications" testid="platform-panel-comms">
            <div className="border border-zinc-100 rounded-md p-4 space-y-2.5">
              {[
                ["Application received", "Instant, automatic"],
                ["Shortlisted for Warehouse Operative", "Sent when you move them"],
                ["Offer letter", "Built from your template, tracked"],
              ].map(([t, n]) => (
                <div key={t} className="flex items-center gap-2.5">
                  <Mail className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
                  <p className="text-xs text-zinc-700 flex-1 truncate">{t}</p>
                  <p className="text-[10px] text-zinc-400 shrink-0">{n}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-zinc-500 mt-4 leading-relaxed">
              Candidates hear from you at every step, branded, from one address, with a full audit trail of what was sent and when.
            </p>
          </Panel>

          <Panel icon={Sparkles} title="AI tools, kept honest" testid="platform-panel-ai">
            <div className="border border-zinc-100 rounded-md p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-3.5 w-3.5 text-violet-600" />
                <p className="text-xs font-semibold text-zinc-950">Job description drafted from your notes</p>
              </div>
              <div className="space-y-1.5" aria-hidden="true">
                <div className="h-2 bg-zinc-100 rounded w-full" />
                <div className="h-2 bg-zinc-100 rounded w-11/12" />
                <div className="h-2 bg-zinc-100 rounded w-4/5" />
              </div>
              <p className="text-[10px] text-zinc-400 mt-2">You review and edit everything before it publishes.</p>
            </div>
            <p className="text-xs text-zinc-500 mt-4 leading-relaxed">
              AI drafts job descriptions and candidate summaries from the details you provide, assistance, not automation. Nothing publishes or sends without you.
            </p>
          </Panel>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-6" data-testid="platform-how">
        <h2 className="font-display text-2xl sm:text-3xl tracking-tight font-black text-zinc-950">How it works</h2>
        <div className="mt-6 grid sm:grid-cols-3 gap-4">
          {[
            ["1", "Post the vacancy", "Free to advertise a job. Every listing is read by a person before it goes live, which keeps quality high for candidates and employers alike."],
            ["2", "Applicants come to you", "Your role is indexed for Google Jobs, boosted across nearby towns you choose, and optionally shared to partner boards with your consent."],
            ["3", "Hire from one pipeline", "Review, shortlist, message, interview and offer without leaving your dashboard. The whole history stays with each candidate."],
          ].map(([n, t, d]) => (
            <div key={n} className="glass rounded-lg p-6">
              <p className="font-display text-3xl font-black text-blue-700">{n}</p>
              <p className="mt-3 text-sm font-semibold text-zinc-950">{t}</p>
              <p className="mt-1.5 text-xs text-zinc-500 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
        <p className="mt-10 text-sm text-zinc-600 leading-relaxed" data-testid="platform-seo-copy">
          If you need to hire staff in the UK without agency fees, HiredToday is affordable job advertising that behaves
          like a full recruitment system: free job advertising for your first listing, then simple flat prices only when
          you want more visibility. Candidates search live vacancies from verified employers by job title, location,
          salary, job type or remote-working options, which means your role is put in front of people genuinely looking
          for it.
        </p>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-20" data-testid="platform-cta">
        <div className="bg-zinc-950 rounded-lg p-8 sm:p-12 text-center">
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight font-black text-white">Try it with your next vacancy.</h2>
          <p className="mt-3 text-sm text-zinc-400 max-w-lg mx-auto leading-relaxed">
            Your first listing is free and takes minutes. If it works, and we think it will, upgrades are there when you need them.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/post-a-job" data-testid="platform-bottom-post"
              className="h-12 px-8 bg-white text-zinc-950 text-sm font-semibold rounded-md hover:bg-zinc-200 transition-colors duration-200 inline-flex items-center justify-center gap-2">
              Post a job free <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/plans" data-testid="platform-bottom-plans"
              className="h-12 px-8 border border-zinc-700 text-white text-sm font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200 inline-flex items-center justify-center">
              Compare subscription plans
            </Link>
          </div>
          <p className="mt-6 text-xs text-zinc-500 inline-flex items-center gap-1.5">
            <BellRing className="h-3.5 w-3.5" /> Jobseekers: create a free account for matched job alerts.
            <Link to="/register" className="underline hover:text-zinc-300" data-testid="platform-bottom-register">Register here</Link>
          </p>
        </div>
      </section>
    </main>
  );
}

export const _unused = { BriefcaseBusiness };
